import React, { useState, useEffect, useCallback } from 'react';
import { getBrokers, setActiveBroker } from '../../api/client';

/**
 * Multi-broker Phase 1 selector. Lists supported brokers, shows the active one,
 * and lets the user switch (only Fyers is selectable today; the rest are
 * "Coming soon"). The backend rejects selecting an unimplemented broker.
 */
export default function BrokerSelector() {
  const [brokers, setBrokers] = useState([]);
  const [active, setActive]   = useState(null);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');

  const load = useCallback(async () => {
    try {
      const res = await getBrokers();
      setBrokers(res.data.brokers || []);
      setActive(res.data.active || null);
    } catch { /* silent — dashboard still works */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSelect = async (id) => {
    if (id === active) return;
    setBusy(true); setMsg('');
    try {
      await setActiveBroker(id);
      setActive(id);
      setMsg('Active broker updated.');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not switch broker.');
    } finally { setBusy(false); }
  };

  if (!brokers.length) return null;

  return (
    <div style={{
      background: 'var(--card, #1a1a1a)', border: '1px solid var(--border, #333)',
      borderRadius: 12, padding: 16, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>
          Broker
        </div>
        {msg && <span style={{ fontSize: 12, color: msg.includes('updated') ? '#22c55e' : '#f59e0b' }}>{msg}</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {brokers.map(b => {
          const isActive = b.id === active;
          const disabled = b.status !== 'available' || busy;
          return (
            <button
              key={b.id}
              onClick={() => handleSelect(b.id)}
              disabled={disabled}
              title={b.status === 'available' ? `Use ${b.label}` : `${b.label} — coming soon`}
              style={{
                padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
                border: isActive ? '1px solid #22c55e' : '1px solid #333',
                background: isActive ? 'rgba(34,197,94,0.12)' : 'transparent',
                color: isActive ? '#22c55e' : (b.status === 'available' ? '#ddd' : '#666'),
                opacity: b.status === 'available' ? 1 : 0.6,
              }}
            >
              {isActive ? '● ' : ''}{b.label}
              {b.status !== 'available' && (
                <span style={{ fontSize: 10, color: '#777', marginLeft: 6 }}>soon</span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: '#666', marginTop: 10 }}>
        Single active broker — orders + market data route through the selected one.
        Switching to a new broker (when available) needs that broker's login and an engine restart.
      </div>
    </div>
  );
}
